/**
 * Internal hooks and pure helpers extracted from data-table.tsx to keep that file under the line limit.
 * Not exported from the package index — used only by DataTable.
 */
import * as React from 'react';
import { cn } from '../lib/utils.js';

export const DATA_TABLE_MIN_COLUMN_WIDTH_PX = 60;

// ─── resolveColResizable ──────────────────────────────────────

/**
 * Determine whether a column is resizable.
 * Column-level flag wins; falls back to the table-level default (true).
 */
export function resolveColResizable(colFlag: boolean | undefined, tableDefault: boolean): boolean {
  return colFlag ?? tableDefault;
}

// ─── alignClass ────────────────────────────────────────────────

/** Resolve a column's `align` value to its Tailwind text-alignment class. */
export function alignClass(align: string): string {
  return align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left';
}

// ─── Virtualization layout resolvers ──────────────────────────
// Pulled into their own functions (own complexity scope) so DataTable's top-level render
// body doesn't accumulate a branch per virtualization-conditional style/className.

/** Resolve DataTable's own scroll-shadow-indicator ref: the caller's when virtualized. */
export function resolveScrollContainerRef(
  virtualizerRef: React.RefObject<HTMLDivElement | null> | undefined,
  localRef: React.RefObject<HTMLDivElement | null>,
): React.RefObject<HTMLDivElement | null> {
  return virtualizerRef ? virtualizerRef : localRef;
}

export interface VirtualizedLayout {
  /** Class applied to DataTable's outer wrapper div (sibling of ScrollIndicators). */
  wrapperClassName: string | undefined;
  scrollContainerClassName: string;
  headerClassName: string | undefined;
  bodyStyle: React.CSSProperties | undefined;
}

/**
 * Resolve the sticky-header/bounded-container/absolute-row styling for virtualized mode.
 *
 * The scroll container is sized via flex (`flex-1 min-h-0`) rather than a hardcoded
 * `calc(100vh - Npx)` — a fixed viewport offset can't account for the actual height of
 * whatever renders above the table (toolbar, filters, banners), which caused the page
 * and the table to scroll independently (two scrollbars) whenever that offset was wrong.
 * The caller's page layout is responsible for giving DataTable's own flex parent a
 * bounded height (e.g. `flex-1 min-h-0` on an ancestor) so this flex sizing has
 * something to fill.
 */
export function resolveVirtualizedLayout(
  isVirtualized: boolean,
  totalSize: number | undefined,
): VirtualizedLayout {
  return {
    wrapperClassName: isVirtualized ? 'flex min-h-0 flex-col' : undefined,
    scrollContainerClassName: cn(
      'overflow-x-auto rounded-lg border border-border bg-background',
      isVirtualized && 'min-h-0 flex-1 overflow-y-auto',
    ),
    headerClassName: isVirtualized ? 'sticky top-0 z-10' : undefined,
    bodyStyle: isVirtualized
      ? { height: totalSize, position: 'relative', display: 'block' }
      : undefined,
  };
}

// ─── resolveStickyHeaderOffset ─────────────────────────────────

export interface StickyHeaderOffsetProps {
  className: string | undefined;
  style: React.CSSProperties | undefined;
}

/**
 * Resolve the sticky-header class/style for the offset-based (non-virtualized) sticky
 * header mode. Kept separate from `resolveVirtualizedLayout`'s virtualized branch on
 * purpose — that branch also switches on row virtualization and a bounded scroll
 * container, which callers passing only an offset (page itself owns the scroll) must
 * NOT get.
 */
export function resolveStickyHeaderOffset(offsetPx: number | undefined): StickyHeaderOffsetProps {
  if (offsetPx === undefined) return { className: undefined, style: undefined };
  return { className: 'sticky z-10', style: { top: offsetPx } };
}

/**
 * Frozen-header mode ("frozen" = a header rendered outside the horizontal-scroll
 * wrapper, kept in sync with it via JS): `overflow-x-auto` on that wrapper forces the
 * browser to treat it as a scroll container on BOTH axes (per the CSS Overflow spec, an
 * ancestor with any non-`visible` overflow value clamps `position: sticky` against
 * itself, not the real page scroller) — so a `<thead>` living inside it can never stick
 * to the page. `virtualizer` mode owns its own bounded, actually-scrollable container
 * and its sticky header legitimately sticks to THAT, so it's excluded here.
 */
export function resolveUseFrozenHeader(
  stickyHeaderOffsetPx: number | undefined,
  virtualizer: unknown,
): boolean {
  return stickyHeaderOffsetPx !== undefined && !virtualizer;
}

export interface FrozenHeaderLayout {
  /** Class for the frozen header's own sticky wrapper box (its `top` comes from `stickyHeaderOffset.style`). */
  frozenWrapperClassName: string;
  /**
   * Class for the real `<thead>` inside the scrolling table — `invisible` (spacer-only)
   * when frozen, or the virtualizer's own sticky class when virtualized. The offset
   * className/style is intentionally NEVER applied here (only to the frozen wrapper
   * above): with a `virtualizer` also present, `stickyHeaderOffsetPx` has no effect
   * (see `resolveUseFrozenHeader`) — applying its inline `top` to this thead would
   * otherwise override the virtualizer's own `top-0` class via style specificity.
   */
  realHeaderClassName: string | undefined;
  /** Extra class for the horizontal-scroll wrapper so its own border doesn't double up with the frozen header's. */
  scrollContainerExtraClassName: string | undefined;
}

/**
 * Resolve every className split between the real thead and its frozen copy — pulled
 * out of DataTable's own render body (own complexity scope) for the same reason
 * `resolveVirtualizedLayout` above is: JSX conditionals in the returned tree count
 * directly against that function's cyclomatic complexity budget.
 */
export function resolveFrozenHeaderLayout(
  useFrozenHeader: boolean,
  virtualizedHeaderClassName: string | undefined,
  stickyHeaderOffset: StickyHeaderOffsetProps,
): FrozenHeaderLayout {
  return {
    frozenWrapperClassName: cn(
      'overflow-hidden rounded-t-lg border-x border-t border-border',
      stickyHeaderOffset.className,
    ),
    realHeaderClassName: cn(virtualizedHeaderClassName, useFrozenHeader && 'invisible'),
    scrollContainerExtraClassName: useFrozenHeader ? 'rounded-t-none border-t-0' : undefined,
  };
}

// ─── useFrozenHeaderSync ───────────────────────────────────────

export interface FrozenHeaderSync {
  frozenHeaderTableRef: React.RefObject<HTMLTableElement | null>;
  measuredHeaderWidths: Record<string, number>;
}

/**
 * Keeps a "frozen" header (rendered outside the horizontal-scroll wrapper — see
 * `stickyHeaderOffsetPx` in data-table.tsx) column-aligned and horizontally in sync with
 * the real, scrolling body table. Two independent things need syncing:
 *  1. Column widths — mirrored from the real (invisible) thead's actual rendered `<th>`
 *     widths, since natural-layout columns (no explicit width) can only be known once
 *     the browser has laid that thead out.
 *  2. Horizontal scroll position — mirrored via `transform`, set imperatively (not React
 *     state) so scrolling doesn't trigger a re-render on every frame.
 * No-ops entirely when `enabled` is false.
 */
export function useFrozenHeaderSync<T extends { id: string }>(
  enabled: boolean,
  theadRef: React.RefObject<HTMLTableSectionElement | null>,
  scrollRef: React.RefObject<HTMLDivElement | null>,
  columns: T[],
): FrozenHeaderSync {
  const frozenHeaderTableRef = React.useRef<HTMLTableElement>(null);
  const [measuredHeaderWidths, setMeasuredHeaderWidths] = React.useState<Record<string, number>>(
    {},
  );

  // No deps: re-measures after every commit so a data/column/resize change that alters
  // a natural-layout column's width is always picked up. useLayoutEffect (not
  // useEffect) applies the result before paint, avoiding a flash of stale widths.
  React.useLayoutEffect(() => {
    if (!enabled) return;
    const next = snapshotThWidths(theadRef, columns);
    setMeasuredHeaderWidths((prev) => (haveEqualWidths(prev, next) ? prev : next));
  });

  React.useEffect(() => {
    if (!enabled) return;
    function remeasure() {
      const next = snapshotThWidths(theadRef, columns);
      setMeasuredHeaderWidths((prev) => (haveEqualWidths(prev, next) ? prev : next));
    }
    window.addEventListener('resize', remeasure);
    return () => window.removeEventListener('resize', remeasure);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- theadRef/columns intentionally excluded, see remeasure's own no-deps sibling effect above
  }, [enabled]);

  React.useEffect(() => {
    if (!enabled) return;
    const scrollEl = scrollRef.current;
    const headerTable = frozenHeaderTableRef.current;
    if (!scrollEl || !headerTable) return;
    // TS doesn't carry the null-narrowing above into this nested function declaration.
    function syncHorizontalScroll() {
      if (!scrollEl || !headerTable) return;
      headerTable.style.transform = `translateX(${-scrollEl.scrollLeft}px)`;
    }
    syncHorizontalScroll();
    scrollEl.addEventListener('scroll', syncHorizontalScroll);
    return () => scrollEl.removeEventListener('scroll', syncHorizontalScroll);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- scrollRef is a stable ref object
  }, [enabled]);

  return { frozenHeaderTableRef, measuredHeaderWidths };
}

// ─── resolveCellClassName ─────────────────────────────────────

/**
 * Resolve a column's `cellClassName` for a given row item.
 * Accepts either a static string (applied to every row) or a function of the row item.
 */
export function resolveCellClassName<T>(
  cellClassName: string | ((item: T) => string) | undefined,
  item: T,
): string | undefined {
  return typeof cellClassName === 'function' ? cellClassName(item) : cellClassName;
}

/** Resolve a `<td>`'s inline style from its resolved pixel width plus any per-row cellStyle. */
export function resolveCellStyle(
  width: number | undefined,
  cellStyle: React.CSSProperties | undefined,
): React.CSSProperties | undefined {
  if (!width && !cellStyle) return undefined;
  return { ...(width ? { width, minWidth: width, maxWidth: width } : null), ...cellStyle };
}

/** Resolve a `<th>`'s inline style from its resolved pixel width. */
export function resolveHeaderStyle(width: number | undefined): React.CSSProperties {
  return width
    ? { width, minWidth: width, maxWidth: width, position: 'relative' }
    : { position: 'relative' };
}

// ─── resolveSortClickHandler ──────────────────────────────────

/**
 * Build the header `onClick` handler for a sortable column, or `undefined` when the column
 * isn't sortable/wired. While `sortOrders` is non-empty (multi-sort badges are showing), a
 * plain click is display-only; a Shift-click always reaches `onSort`.
 */
export function resolveSortClickHandler(
  col: { sortable?: boolean; sortKey?: string },
  onSort: ((sortKey: string, shiftKey: boolean) => void) | undefined,
  sortOrders?: unknown[],
): ((e: React.MouseEvent) => void) | undefined {
  const sortKey = col.sortKey;
  if (!col.sortable || !sortKey || !onSort) return undefined;
  return (e: React.MouseEvent) => {
    const multiSortActive = Boolean(sortOrders && sortOrders.length > 0);
    if (multiSortActive && !e.shiftKey) return;
    onSort(sortKey, e.shiftKey);
  };
}

// ─── snapshotThWidths ────────────────────────────────────────

/**
 * Snapshot current pixel widths of all <th> elements from the DOM.
 *
 * Reads `data-col-id` first, falling back to `data-measure-col-id` — the invisible,
 * measurement-only spacer copy of a header cell (rendered inside `theadRef`'s thead
 * alongside a frozen header) carries the latter instead of the former, so e2e specs'
 * `th[data-col-id="..."]` selectors keep matching only the one real, visible cell.
 */
export function snapshotThWidths<T extends { id: string }>(
  theadRef: React.RefObject<HTMLTableSectionElement | null>,
  _columns: T[],
): Record<string, number> {
  const thead = theadRef.current;
  if (!thead) return {};
  const snapshot: Record<string, number> = {};
  const ths = thead.querySelectorAll('th');
  ths.forEach((th) => {
    const id = th.getAttribute('data-col-id') ?? th.getAttribute('data-measure-col-id');
    if (!id) return;
    if (th.offsetWidth > 0) snapshot[id] = th.offsetWidth;
  });
  return snapshot;
}

// ─── haveEqualWidths ─────────────────────────────────────────

/** Shallow-equality check for two column-width snapshots (same keys, same values). */
export function haveEqualWidths(a: Record<string, number>, b: Record<string, number>): boolean {
  const aKeys = Object.keys(a);
  if (aKeys.length !== Object.keys(b).length) return false;
  return aKeys.every((key) => a[key] === b[key]);
}

// ─── computeTableWidthInfo ───────────────────────────────────

/**
 * Returns true when every column has a resolved (explicit or resized) width.
 * When true, the table uses fixed layout and an exact pixel total width.
 */
export function computeTableWidthInfo<T extends { id: string; width?: number }>(
  columns: T[],
  colWidths: Record<string, number>,
): { allColsHaveWidths: boolean; totalTableWidth: number | undefined } {
  const resolveW = (col: T) => colWidths[col.id] ?? col.width;
  const allColsHaveWidths = columns.every((col) => resolveW(col) !== undefined);
  const totalTableWidth = allColsHaveWidths
    ? columns.reduce((sum, col) => sum + (resolveW(col) ?? 0), 0)
    : undefined;
  return { allColsHaveWidths, totalTableWidth };
}

// ─── buildRowPropsFor ────────────────────────────────────────

export interface RowPropsContext<T> {
  onRowClick?: (item: T) => void;
  getRowClassName?: (item: T, index: number) => string | undefined;
  draggableRows?: boolean;
  dragOverRowIndex: number | null;
  /**
   * True when `renderRowLink` is set — adds `position: relative` to the row so the
   * row-link anchor's stretched `::after` pseudo-element (position: absolute; inset: 0)
   * covers the whole row instead of just its own near-zero-width cell. See
   * `ROW_LINK_ANCHOR_CLASSNAME` in data-table-helpers.tsx for the full mechanism.
   */
  hasRowLink?: boolean;
}

export function buildRowPropsFor<T>(item: T, idx: number, ctx: RowPropsContext<T>) {
  const { onRowClick, getRowClassName, draggableRows, dragOverRowIndex, hasRowLink } = ctx;
  const isClickable = !!onRowClick;
  const extraClass = getRowClassName?.(item, idx);
  const isDragTarget = dragOverRowIndex === idx && dragOverRowIndex !== null;
  return {
    isClickable,
    className: cn(
      'border-b border-border/60 hover:bg-muted/30 transition-colors',
      idx % 2 === 0 ? 'bg-background' : 'bg-muted/10',
      isClickable && 'cursor-pointer',
      hasRowLink && 'relative',
      draggableRows && 'cursor-default',
      isDragTarget && 'border-t-2 border-t-primary',
      extraClass,
    ),
    onClick: isClickable ? () => onRowClick?.(item) : undefined,
    onKeyDown: isClickable
      ? (e: React.KeyboardEvent) => {
          if (e.key === 'Enter') onRowClick?.(item);
        }
      : undefined,
  };
}

// ─── resolveRowLinkProps ──────────────────────────────────────

/**
 * Resolves whether `renderRowLink` is active and, if so, drops `onRowClick` (it
 * supersedes the legacy `<tr onClick>` mechanism — never wire both onto the same row).
 * Extracted out of DataTable's own render body purely to keep that function's cyclomatic
 * complexity under budget; the logic itself is a single conditional.
 */
export function resolveRowLinkProps<T>(
  renderRowLink: ((item: T) => React.ReactNode) | undefined,
  onRowClick: ((item: T) => void) | undefined,
): { hasRowLink: boolean; effectiveOnRowClick: ((item: T) => void) | undefined } {
  const hasRowLink = Boolean(renderRowLink);
  return { hasRowLink, effectiveOnRowClick: hasRowLink ? undefined : onRowClick };
}

// ─── useColumnResize ─────────────────────────────────────────

interface UseColumnResizeOptions {
  initialColumns: { id: string; width?: number }[];
  /** Called once on mouseup with the dragged column's final pixel width — never during mousemove. */
  onResizeEnd?: (colId: string, width: number) => void;
}

export interface UseColumnResizeResult {
  colWidths: Record<string, number>;
  isResizing: boolean;
  /**
   * Start a column resize drag.
   * @param snapshotWidths  Current pixel widths for ALL columns measured from the DOM just
   *                        before drag start. Committing them all at once prevents other
   *                        columns from jumping when the table switches to fixed layout.
   */
  handleResizeStart: (
    e: React.MouseEvent,
    colId: string,
    snapshotWidths?: Record<string, number>,
  ) => void;
}

// Attaches temporary mousemove/mouseup listeners for a resize drag session.
function attachResizeListeners(
  resizeRef: React.MutableRefObject<{ colId: string; startX: number; startWidth: number } | null>,
  setColWidths: React.Dispatch<React.SetStateAction<Record<string, number>>>,
  setIsResizing: React.Dispatch<React.SetStateAction<boolean>>,
  onResizeEnd?: (colId: string, width: number) => void,
) {
  // Captured once at listener-attach time (resizeRef.current is already set by the
  // caller) rather than read inside onMouseUp, which nulls the ref before this can
  // run. Updated on every mousemove so mouseup always reports the final width.
  const dragColId = resizeRef.current?.colId;
  let lastWidth = resizeRef.current?.startWidth;

  function onMouseMove(ev: MouseEvent) {
    if (!resizeRef.current) return;
    // Destructure immediately — resizeRef.current may be nulled by mouseup before
    // the React state updater function executes (batched updates race condition).
    const { colId, startX, startWidth } = resizeRef.current;
    const delta = ev.clientX - startX;
    const newWidth = Math.max(DATA_TABLE_MIN_COLUMN_WIDTH_PX, startWidth + delta);
    lastWidth = newWidth;
    setColWidths((prev) => ({ ...prev, [colId]: newWidth }));
  }
  function onMouseUp() {
    resizeRef.current = null;
    setIsResizing(false);
    document.removeEventListener('mousemove', onMouseMove);
    document.removeEventListener('mouseup', onMouseUp);
    if (dragColId !== undefined && lastWidth !== undefined) onResizeEnd?.(dragColId, lastWidth);
  }
  document.addEventListener('mousemove', onMouseMove);
  document.addEventListener('mouseup', onMouseUp);
}

// ─── Resize-start helpers (extracted to keep useColumnResize under line limit) ─

/** Resolve the starting width for the column being dragged. */
function resolveResizeStartWidth(
  colId: string,
  storedWidths: Record<string, number>,
  th: HTMLTableCellElement | null,
  snapshotWidths?: Record<string, number>,
): number {
  const stored = storedWidths[colId];
  if (stored !== undefined) return stored;
  const dom = th?.offsetWidth ?? 0;
  return dom > 0 ? dom : (snapshotWidths?.[colId] ?? 120);
}

/** Merge snapshot widths into stored widths, preserving any already-resized values. */
function mergeSnapshotIntoWidths(
  snapshot: Record<string, number>,
  setColWidths: React.Dispatch<React.SetStateAction<Record<string, number>>>,
): void {
  if (Object.keys(snapshot).length === 0) return;
  setColWidths((prev) => ({ ...snapshot, ...prev }));
}

// ─── useColumnResize ─────────────────────────────────────────

export function useColumnResize({
  initialColumns,
  onResizeEnd,
}: UseColumnResizeOptions): UseColumnResizeResult {
  const [colWidths, setColWidths] = React.useState<Record<string, number>>(() => {
    const init: Record<string, number> = {};
    for (const col of initialColumns) {
      if (col.width !== undefined) init[col.id] = col.width;
    }
    return init;
  });

  const [isResizing, setIsResizing] = React.useState(false);
  const resizeRef = React.useRef<{ colId: string; startX: number; startWidth: number } | null>(
    null,
  );

  const prevColIdsRef = React.useRef<string>('');
  React.useEffect(() => {
    const newIds = initialColumns.map((c) => c.id).join(',');
    if (newIds === prevColIdsRef.current) return;
    prevColIdsRef.current = newIds;
    setColWidths((prev) => {
      const next: Record<string, number> = {};
      for (const col of initialColumns) {
        if (col.id in prev) next[col.id] = prev[col.id]!;
        else if (col.width !== undefined) next[col.id] = col.width;
      }
      return next;
    });
  }, [initialColumns]);

  function handleResizeStart(
    e: React.MouseEvent,
    colId: string,
    snapshotWidths?: Record<string, number>,
  ) {
    e.preventDefault();
    e.stopPropagation();
    const th = (e.currentTarget as HTMLElement).closest('th') as HTMLTableCellElement | null;
    const startWidth = resolveResizeStartWidth(colId, colWidths, th, snapshotWidths);
    if (snapshotWidths) mergeSnapshotIntoWidths(snapshotWidths, setColWidths);
    resizeRef.current = { colId, startX: e.clientX, startWidth };
    setIsResizing(true);
    attachResizeListeners(resizeRef, setColWidths, setIsResizing, onResizeEnd);
  }

  return { colWidths, isResizing, handleResizeStart };
}

// ─── useRowDragDrop ──────────────────────────────────────────

interface UseRowDragDropOptions {
  onRowReorder?: (fromIndex: number, toIndex: number) => void;
}

export interface UseRowDragDropResult {
  dragOverRowIndex: number | null;
  handleRowDragStart: (e: React.DragEvent, index: number) => void;
  handleRowDragOver: (e: React.DragEvent, index: number) => void;
  handleRowDrop: (e: React.DragEvent, toIndex: number) => void;
  handleRowDragEnd: () => void;
}

export function useRowDragDrop({ onRowReorder }: UseRowDragDropOptions): UseRowDragDropResult {
  const dragRowIndexRef = React.useRef<number | null>(null);
  const [dragOverRowIndex, setDragOverRowIndex] = React.useState<number | null>(null);

  function handleRowDragStart(e: React.DragEvent, index: number) {
    dragRowIndexRef.current = index;
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(index));
  }

  function handleRowDragOver(e: React.DragEvent, index: number) {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverRowIndex(index);
  }

  function handleRowDrop(e: React.DragEvent, toIndex: number) {
    e.preventDefault();
    const fromIndex = dragRowIndexRef.current;
    if (fromIndex !== null && fromIndex !== toIndex) {
      onRowReorder?.(fromIndex, toIndex);
    }
    dragRowIndexRef.current = null;
    setDragOverRowIndex(null);
  }

  function handleRowDragEnd() {
    dragRowIndexRef.current = null;
    setDragOverRowIndex(null);
  }

  return {
    dragOverRowIndex,
    handleRowDragStart,
    handleRowDragOver,
    handleRowDrop,
    handleRowDragEnd,
  };
}
